'use client';

import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { HTMLAttributes, forwardRef, useId, useState } from 'react';

import {
  Input,
  InputDescription,
  InputDescriptionProps,
  InputLabel,
  InputLabelProps,
  InputProps,
} from '../input';
import cn from '../lib/cn';

export type PasswordInputProps = Omit<InputProps, 'type' | 'rightSection'> & {
  label?: React.ReactNode;
  description?: React.ReactNode;
  labelProps?: InputLabelProps;
  descriptionProps?: InputDescriptionProps;
  classNames?: {
    root?: string;
    label?: string;
    description?: string;
  };
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
};

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      id,
      label,
      description,
      labelProps,
      descriptionProps,
      disabled,
      readOnly,
      wrapperProps,
      classNames,
      ...props
    }: PasswordInputProps,
    ref,
  ) => {
    const internalId = useId();
    const [displayPassword, setDisplayPassword] = useState(false);

    return (
      <div
        {...wrapperProps}
        className={cn(
          'flex flex-col',
          classNames?.root,
          wrapperProps?.className,
        )}
      >
        {label && (
          <InputLabel
            {...labelProps}
            className={cn('mb-2', labelProps?.className, classNames?.label)}
            disabled={disabled}
            readOnly={readOnly}
            htmlFor={id || internalId}
          >
            {label}
          </InputLabel>
        )}
        <Input
          {...props}
          ref={ref}
          id={id || internalId}
          disabled={disabled}
          readOnly={readOnly}
          type={displayPassword ? 'text' : 'password'}
          rightSection={
            <button
              type="button"
              onClick={() => setDisplayPassword((prev) => !prev)}
              className={cn(
                disabled || readOnly
                  ? 'text-content-tertiary'
                  : 'text-content-primary',
              )}
            >
              {displayPassword ? (
                <EyeOffIcon size={16} />
              ) : (
                <EyeIcon size={16} />
              )}
            </button>
          }
        />
        {description && (
          <InputDescription
            {...descriptionProps}
            variant={props.variant}
            className={cn(
              'mt-2',
              descriptionProps?.className,
              classNames?.description,
            )}
            disabled={disabled}
            readOnly={readOnly}
          >
            {description}
          </InputDescription>
        )}
      </div>
    );
  },
);
PasswordInput.displayName = 'TextInput';

export default PasswordInput;
